<template>
  <main class="max-w-screen-2xl mx-auto">
    <artwork-wizard-uploader
      v-if="openStepper"
      :show-modal="openStepper"
      @close="closeStepperDialog"
    />
    <div class="w-full hidden md:block md:px-16 xl:px-48">
      <DesktopNavbar @uploadArt="showStepperDialog" />
    </div>
    <div
      class="w-full block md:hidden"
      style="padding-left: 4.9%; padding-right: 4.9%"
    >
      <MobileNavbar  @uploadArt="showStepperDialog" />
    </div>
    <div class="w-full px-2 sm:px-6 pt-0 sm:pt-12">
      <router-view />
    </div>
  </main>
</template>

<script>
import DesktopNavbar from "../components/navigation/DesktopNavbar.vue";
import MobileNavbar from "../components/navigation/MobileNavbar.vue";
import ArtworkWizardUploader from "../components/ArtworkWizardUploader";
import {
  WIZARD_PATH,
  isWizardRoute,
  resolveWizardExit,
} from "../features/arts/wizard-route";

// The upload wizard is open exactly when the URL says '/add', and nowhere
// else. It used to be a local boolean here, which was fine while the only way
// in was the navbar button -- but the WordPress site needs a link, and the
// moment a route can also open it, a boolean and a URL are two sources of
// truth that disagree the first time anyone presses Back. Deriving the flag
// from the route is what makes the deep link, the button and the browser's
// history agree without any of them knowing about each other.
//
// The navbars are unchanged: they still just $emit("uploadArt") and know
// nothing about routing (DesktopNavbar.vue:131, MobileNavbar.vue:162). This
// component is the only place that turns that intent into a navigation.
export default {
  name: "LayoutBase",
  components: {
    DesktopNavbar,
    MobileNavbar,
    ArtworkWizardUploader,
  },
  data: () => {
    return {
      // Route changes performed since this SPA booted. Zero means the visitor
      // has been on exactly one URL since the page loaded, which is the only
      // reliable way to tell someone who arrived cold on a WordPress link from
      // someone who opened the wizard mid-session -- see resolveWizardExit.
      //
      // Counted here rather than in the router because the $route watcher
      // below is created AFTER the initial route resolves, so the cold arrival
      // never increments it. A router.afterEach hook would fire for the
      // initial navigation too and need an off-by-one to undo.
      inAppNavigations: 0,
    };
  },
  computed: {
    openStepper() {
      return isWizardRoute(this.$route.path);
    },
  },
  watch: {
    $route() {
      this.inAppNavigations += 1;
    },
  },
  methods: {
    showStepperDialog: function () {
      // Already there: pushing '/add' onto '/add' rejects with
      // NavigationDuplicated in vue-router 3.1.3, which surfaces as an
      // unhandled rejection and nothing else.
      if (this.openStepper) return;

      // The query carries the visitor's filters and the campaign parameters
      // their link arrived with. The wizard is an overlay on the page behind
      // it, so it must not silently reset either -- and keeping the query
      // identical is what lets HomePage skip a refetch (isWizardToggle).
      this.$router.push({ path: WIZARD_PATH, query: this.$route.query });
    },
    closeStepperDialog: function () {
      // Deferred by one tick on purpose. The wizard also emits "close" on a
      // SUCCESSFUL submit and then immediately pushes the new piece's page
      // (ArtworkWizardUploader.vue:546-547). Acting synchronously would race
      // that: router.back() is a history.go(-1) that lands AFTER the push, so
      // the visitor would be thrown off the artwork they just created and back
      // onto the page before the wizard. By next tick that push has already
      // resolved -- neither route in this app is an async component, so
      // vue-router completes the transition synchronously -- the route is no
      // longer '/add', and there is nothing left for us to close.
      this.$nextTick(() => {
        if (!this.openStepper) return;

        const exit = resolveWizardExit(this.inAppNavigations);

        // Back, not a push to the previous path: it is the only exit that
        // restores the exact page they were on, scroll position and map
        // viewport included, without a request.
        if (exit.action === "back") {
          this.$router.back();
          return;
        }

        this.$router.replace({ path: exit.path, query: this.$route.query });
      });
    },
  },
};
</script>
